package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.DeletedAccount
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.data.dto.isDeletedAccount
import com.arena_web.data.dto.FollowUserPageDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.TextInput

private const val DIALOG_BG = "#09142d"
private const val ROW_DIVIDER = "0.5px solid rgba(255, 255, 255, 0.06)"
private const val FOLLOW_LIST_PAGE_SIZE = 20
private const val FOLLOW_LIST_SEARCH_DEBOUNCE_MS = 300L

/** Visual mode for the dialog — drives title label only; row buttons come from caller. */
enum class FollowListMode { Followers, Following }

/** Mask-composite gradient border overlay. Parent must be `position: relative`. */
@Composable
private fun GradientBorder(radiusPx: Int, gradient: String = AppGradients.ButtonBorderGradientPrimary) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "1px solid transparent")
                property("border-radius", s(radiusPx))
                property("background", "$gradient border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
    )
}

/**
 * Generic Followers / Following list dialog matching Figma. Caller renders the
 * per-row action button via [actionForUser] (omit for read-only rows).
 *
 * The dialog owns its own pagination, search debounce and loading state — the
 * caller just hands over a [loadPage] adapter that wraps the right endpoint
 * (my followers / my following / target user's followers / target user's
 * following). [initialCount] is shown in the count badge before the first
 * page resolves; it's overwritten with the server's `totalItems` once the
 * fetch lands.
 */
@Composable
fun FollowListDialog(
    mode: FollowListMode,
    initialCount: Long,
    loadPage: suspend (page: Int, search: String?) -> Result<FollowUserPageDto>,
    onUserClick: (FollowUserDto) -> Unit,
    onDismiss: () -> Unit,
    actionForUser: @Composable (FollowUserDto) -> Unit = {}
) {
    var users by remember { mutableStateOf<List<FollowUserDto>>(emptyList()) }
    var count by remember { mutableStateOf(initialCount) }
    var currentPage by remember { mutableStateOf(0) }
    var hasMore by remember { mutableStateOf(true) }
    var isLoading by remember { mutableStateOf(true) }
    var isLoadingMore by remember { mutableStateOf(false) }
    var searchQuery by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()

    val t = LocalAppStrings.current
    val title = if (mode == FollowListMode.Followers) t.followListFollowers else t.followListFollowing

    // Debounced server search. Every keystroke resets pagination and waits
    // for the user to pause before firing — keeps results aligned with the
    // input box without spamming the API.
    LaunchedEffect(searchQuery) {
        delay(FOLLOW_LIST_SEARCH_DEBOUNCE_MS)
        isLoading = true
        users = emptyList()
        currentPage = 0
        hasMore = true
        loadPage(1, searchQuery.takeIf { it.isNotBlank() })
            .onSuccess { page ->
                users = page.items
                count = page.totalItems
                currentPage = page.page
                hasMore = page.page < page.totalPages
            }
            .onFailure { hasMore = false }
        isLoading = false
    }

    val loadMore: () -> Unit = loadMore@{
        if (isLoading || isLoadingMore || !hasMore) return@loadMore
        isLoadingMore = true
        val nextPage = currentPage + 1
        val activeQuery = searchQuery
        scope.launch {
            loadPage(nextPage, activeQuery.takeIf { it.isNotBlank() })
                .onSuccess { page ->
                    // Drop the response if the user has typed something new
                    // in the meantime — the debounce effect already started
                    // a fresh search and we don't want to mix results.
                    if (activeQuery != searchQuery) return@onSuccess
                    val existing = users.mapTo(mutableSetOf()) { it.userId }
                    users = users + page.items.filter { it.userId !in existing }
                    currentPage = page.page
                    hasMore = page.page < page.totalPages
                    count = page.totalItems
                }
                .onFailure { hasMore = false }
            isLoadingMore = false
        }
    }

    // Backdrop overlay — click to dismiss.
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("z-index", "1000")
                property("background", "rgba(0, 0, 0, 0.6)")
            }
            .onClick { onDismiss() },
        contentAlignment = Alignment.Center
    ) {
        // Outer dialog wrapper — relative so the gradient border overlay can mount.
        Box(
            modifier = Modifier
                .sWidth(450)
                .sMaxHeight(620)
                .styleModifier {
                    property("position", "relative")
                    property("border-radius", s(18))
                }
                .onClick { it.stopPropagation() }
        ) {
            // Inner content
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("background", DIALOG_BG)
                        property("border-radius", s(18))
                        property("padding", "${s(16)} ${s(22)} ${s(22)} ${s(22)}")
                        property("gap", s(22))
                    }
            ) {
                // Header — title + count badge + close button
                Column(
                    modifier = Modifier.fillMaxWidth().sGap(12)
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sPaddingBottom(12)
                            .styleModifier { property("border-bottom", ROW_DIVIDER) },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Row(
                            modifier = Modifier.sGap(8).flexGrow(1),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                title,
                                modifier = Modifier
                                    .sFontSize(20)
                                    .fontWeight(AppTypography.Medium)
                                    .sLineHeight(26)
                                    .sLetterSpacing(0.2)
                                    .color(AppColors.Text.Secondary)
                            )
                            CountBadge(count)
                        }
                        CloseButton(onClick = onDismiss)
                    }

                    // Search input
                    SearchInput(
                        value = searchQuery,
                        onValueChange = { searchQuery = it }
                    )
                }

                // User list — scrollable. Scroll listener triggers loadMore
                // once the viewer comes within ~120px of the bottom.
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .sGap(22)
                        .styleModifier {
                            property("overflow-y", "auto")
                            property("scrollbar-width", "thin")
                            property("scrollbar-color", "rgba(255, 255, 255, 0.15) transparent")
                            property("min-height", "0")
                        }
                        .attrsModifier {
                            onScroll { evt ->
                                if (hasMore && !isLoading && !isLoadingMore) {
                                    val el = evt.target.unsafeCast<org.w3c.dom.HTMLElement>()
                                    val nearBottom =
                                        el.scrollHeight - el.scrollTop - el.clientHeight < 120.0
                                    if (nearBottom) loadMore()
                                }
                            }
                        }
                ) {
                    when {
                        isLoading -> Box(
                            modifier = Modifier.fillMaxWidth().sPaddingTop(32).sPaddingBottom(32),
                            contentAlignment = Alignment.Center
                        ) { InlineSpinner(size = 24.px) }
                        users.isEmpty() -> EmptyState(
                            if (searchQuery.isNotBlank()) t.followListNoResults
                            else if (mode == FollowListMode.Followers) t.followListNoFollowersYet
                            else t.followListNotFollowingAnyoneYet
                        )
                        else -> {
                            // Skip the divider on the last row (and only that
                            // row) when there's no "Loading more…" spinner
                            // below it — the divider doubles as the visual
                            // seam between rows.
                            users.forEachIndexed { index, user ->
                                val isLast = index == users.lastIndex && !isLoadingMore
                                UserRow(
                                    user = user,
                                    showDivider = !isLast,
                                    onClick = { onUserClick(user) },
                                    action = { actionForUser(user) }
                                )
                            }
                            if (isLoadingMore) {
                                Box(
                                    modifier = Modifier.fillMaxWidth().sPaddingTop(8).sPaddingBottom(8),
                                    contentAlignment = Alignment.Center
                                ) { InlineSpinner(size = 20.px) }
                            }
                        }
                    }
                }
            }
        }
    }
}

// =============================================================================
// Pre-baked button variants — caller composes these inside actionForUser
// =============================================================================

/**
 * Following button used in MY Following list. Toggles between two visual states
 * based on whether the user is still followed in this dialog session:
 *  - currently following  → "Unfollow" outline pill (click → confirm unfollow)
 *  - just unfollowed here → "Follow" filled blue pill (click → re-follow)
 */
@Composable
fun FollowingActionButton(
    isStillFollowing: Boolean,
    onUnfollowRequest: () -> Unit,
    onRefollow: () -> Unit
) {
    val t = LocalAppStrings.current
    if (isStillFollowing) {
        ActionPill(label = t.followListUnfollow, filled = false, onClick = onUnfollowRequest)
    } else {
        ActionPill(label = t.followListFollow, filled = true, onClick = onRefollow)
    }
}

/**
 * Remove button used in MY Followers list. Tracks "Deleted" state for rows
 * that were just removed in this session.
 */
@Composable
fun RemoveFollowerActionButton(
    isDeleted: Boolean,
    onRemoveRequest: () -> Unit
) {
    if (isDeleted) {
        DeletedPill()
    } else {
        ActionPill(label = LocalAppStrings.current.followListRemove, filled = false, onClick = onRemoveRequest)
    }
}

// =============================================================================
// Internal building blocks
// =============================================================================

@Composable
private fun CountBadge(count: Long) {
    val sport = LocalSportColors.current
    Box(
        modifier = Modifier
            .sSize(20)
            .sBorderRadius(100)
            .backgroundColor(sport.primary)
            .overflow(Overflow.Hidden),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            "$count",
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.SemiBold)
                .sLineHeight(14)
                .color(sport.onPrimary)
                .styleModifier { property("transform", "translateY(1px)") }
        )
    }
}

@Composable
private fun CloseButton(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(36)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(100))
                property("background", AppGradients.InteractiveSecondary)
            },
        contentAlignment = Alignment.Center
    ) {
        GradientBorder(100)
        Icon(src = AppIcons.Close, modifier = Modifier.sSize(16))
    }
}

@Composable
private fun SearchInput(value: String, onValueChange: (String) -> Unit) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(44)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(28))
                property("background", AppGradients.InteractiveSecondary)
            }
    ) {
        GradientBorder(28)
        Row(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("padding-left", s(12))
                    property("padding-right", s(8))
                    property("gap", s(6))
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                src = AppIcons.Search,
                modifier = Modifier.sSize(24).styleModifier { property("opacity", "0.5") }
            )
            TextInput(
                value = value,
                attrs = Modifier
                    .fillMaxWidth()
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .color(AppColors.Text.Primary)
                    .styleModifier {
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("font-family", "Urbanist, sans-serif")
                        property("letter-spacing", "0.16px")
                    }
                    .toAttrs {
                        attr("placeholder", t.commonSearch)
                        onInput { e -> onValueChange(e.target.asDynamic().value as String) }
                        style { property("color", "white") }
                    }
            )
        }
    }
}

@Composable
private fun UserRow(
    user: FollowUserDto,
    onClick: () -> Unit,
    action: @Composable () -> Unit,
    showDivider: Boolean = true,
) {
    // Anonymized account: keep the row visible so follower counts still add
    // up, but strip the navigation affordance and replace the label with
    // "Deleted Account" so the disposable `deleted_<uuid>` handle never
    // surfaces in the UI.
    val deleted = user.isDeletedAccount()
    val primaryLine = when {
        deleted -> DeletedAccount.DISPLAY_NAME
        !user.fullName.isNullOrBlank() -> user.fullName
        else -> user.username
    }
    val cityLine = user.city?.takeIf { it.isNotBlank() && !deleted }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingBottom(10)
            .styleModifier {
                if (showDivider) property("border-bottom", ROW_DIVIDER)
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Avatar + name block (username intentionally omitted — the row
        // shows the human name with the optional city as a muted second line).
        Row(
            modifier = Modifier
                .sGap(8)
                .then(
                    if (deleted) Modifier
                    else Modifier.cursor(Cursor.Pointer).onClick { onClick() }
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            CardAvatar(user)
            Column(
                modifier = Modifier.styleModifier {
                    property("gap", s(2))
                    property("min-width", "0")
                }
            ) {
                SpanText(
                    primaryLine,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(18)
                        .sLetterSpacing(0.032)
                        .color(AppColors.Text.Secondary)
                        .whiteSpace(WhiteSpace.NoWrap)
                        .overflow(Overflow.Hidden)
                        .textOverflow(TextOverflow.Ellipsis)
                )
                if (cityLine != null) {
                    SpanText(
                        cityLine,
                        modifier = Modifier
                            .sFontSize(13)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(18)
                            .sLetterSpacing(0.026)
                            .color(AppColors.Text.OnSurfaceMuted)
                            .whiteSpace(WhiteSpace.NoWrap)
                            .overflow(Overflow.Hidden)
                            .textOverflow(TextOverflow.Ellipsis)
                    )
                }
            }
        }

        // Caller-supplied action button — hide for deleted accounts since
        // following / chatting with a ghost shell makes no sense.
        if (!deleted) action()
    }
}

/**
 * 40×60 player-card avatar — same component used in the feed sidebar. Falls
 * back to a rounded rectangle photo tile when the user has no generated card.
 */
@Composable
private fun CardAvatar(user: FollowUserDto) {
    val cardFrameUrl = user.cardImageUrl
    Box(
        modifier = Modifier
            .styleModifier {
                property("flex-shrink", "0")
                property("width", s(40))
                property("height", s(60))
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            },
        contentAlignment = Alignment.Center
    ) {
        if (cardFrameUrl != null) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = "football",
                size = PlayerCardSize.Highlight,
                cardFrameUrl = cardFrameUrl,
                avatarUrl = user.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                hideRating = true,
                hideShadow = true,
            )
        } else {
            val avatarUrl = user.avatarUrl
            Box(
                modifier = Modifier
                    .sWidth(40)
                    .sHeight(60)
                    .styleModifier {
                        property("border-radius", s(6))
                        property("background", "rgba(60, 121, 241, 0.15)")
                        property("overflow", "hidden")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (avatarUrl != null) {
                    Div(attrs = {
                        ref { el ->
                            el.innerHTML =
                                "<img src=\"$avatarUrl\" referrerpolicy=\"no-referrer\" " +
                                    "style=\"width:100%;height:100%;object-fit:cover;display:block;\" />"
                            onDispose { }
                        }
                        style {
                            property("width", "100%")
                            property("height", "100%")
                        }
                    })
                } else {
                    SpanText(
                        user.username.take(1).uppercase(),
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.SemiBold)
                            .color(AppColors.Text.Secondary)
                    )
                }
            }
        }
    }
}

/** Outline / filled action pill — 32px tall, radius 28, px-14 padding. */
@Composable
fun ActionPill(
    label: String,
    filled: Boolean,
    onClick: () -> Unit
) {
    val sport = LocalSportColors.current
    Box(
        modifier = Modifier
            .sHeight(32)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(28))
                property("padding", "0 ${s(14)}")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                if (filled) {
                    property("background", sport.primary.toRgb().let {
                        "rgb(${it.red}, ${it.green}, ${it.blue})"
                    })
                } else {
                    property("background", AppGradients.InteractiveSecondary)
                }
            }
    ) {
        if (!filled) GradientBorder(28)
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .color(if (filled) sport.onPrimary else AppColors.Text.Secondary)
        )
    }
}

/** Disabled "Deleted" pill shown after a follower is removed. */
@Composable
private fun DeletedPill() {
    Box(
        modifier = Modifier
            .sHeight(32)
            .sBorderRadius(28)
            .styleModifier {
                property("padding", "0 ${s(14)}")
                property("background", "rgba(255, 255, 255, 0.08)")
                property("cursor", "default")
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            LocalAppStrings.current.followListDeleted,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .color(AppColors.Text.Muted)
        )
    }
}

@Composable
private fun EmptyState(message: String) {
    Box(
        modifier = Modifier.fillMaxWidth().sPaddingTop(32).sPaddingBottom(32),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            message,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .color(AppColors.Text.Muted)
        )
    }
}
